import { LINKS, ProtocolKey } from "@silo/types";
import { cn } from "../../utils/class-names";
import { DescriptionExternalLink, TooltipDescription } from "../description";
import { Pill } from "../pill";
import { isNullable } from "effect/Predicate";

const mainnetLegacyExample = getExampleCopy("cbETH", "ETH", "XAI");

const EXAMPLE_CONTENT: Record<ProtocolKey, string> = {
  mainnetLegacy: mainnetLegacyExample,
  mainnetLlama: getExampleCopy("rETH", "CRV"),
  mainnetGrand: getExampleCopy("ezETH", "ETH", "USDC"),
  arbitrum: getExampleCopy("ARB", "ETH", "USDC.e"),
  optimism: getExampleCopy("OP", "ETH", "USDC"),
  base: getExampleCopy("tBTC", "ETH", "USDC"),
  lrtbase: getExampleCopy("tBTC", "cbBTC"),
  local: mainnetLegacyExample,
  anvil: mainnetLegacyExample,
};

function getExampleCopy(
  baseSymbol: string,
  firstBridgeSymbol: string,
  secondBridgeSymbol?: string
): string {
  if (isNullable(secondBridgeSymbol)) {
    return `For example, if you deposit ${firstBridgeSymbol} in the ${baseSymbol} silo, others must collateralize ${baseSymbol} to borrow your tokens, and therefore, you are only exposed to the risk of the ${baseSymbol} token asset.`;
  }

  return `For example, if you deposit ${baseSymbol} in the ${baseSymbol} silo, others must collateralize ${firstBridgeSymbol} or ${secondBridgeSymbol} (or both) to borrow your tokens, and therefore, you are only exposed to the risk of ${firstBridgeSymbol}/${secondBridgeSymbol} token assets.`;
}

interface IsolatedRiskContentProps {
  className?: string;
  protocolKey: ProtocolKey;
}

export function IsolatedRiskContent({
  className,
  protocolKey,
}: IsolatedRiskContentProps): JSX.Element {
  const example = EXAMPLE_CONTENT[protocolKey];

  return (
    <div className={cn("flex flex-col gap-5", className)}>
      <p>
        Silo&apos;s lending markets are isolated; An exploit in one market
        doesn&apos;t transfer to others because each market is a separate smart
        contract - risk is isolated.
      </p>

      <p>
        Most lending protocols pool all token assets in one smart contract -
        risk is shared.
      </p>

      <p>
        Risk isolation is fundamental to the safety of your funds. {example}
      </p>

      <DescriptionExternalLink href={LINKS.SILOPEDIA.WHAT_IS_SILO}>
        Learn more about risk isolation
      </DescriptionExternalLink>
    </div>
  );
}

interface IsolatedRiskPillProps {
  className?: string;
  protocolKey: ProtocolKey;
}

export function IsolatedRiskPill({
  className,
  protocolKey,
}: IsolatedRiskPillProps): JSX.Element {
  return (
    <Pill className={className}>
      Isolated risk
      <TooltipDescription placement="right-start">
        <IsolatedRiskContent protocolKey={protocolKey} />
      </TooltipDescription>
    </Pill>
  );
}
